/**
 * Canonical constraint check keys for the Planning domain.
 *
 * Doc 102 §ConstraintCheckResult defines the authoritative key names.
 * All planning constraint evaluations — in planningEngine.ts, planningEngineModule.ts,
 * planningQueries.ts, and the UI — MUST use these constants.
 *
 * Previous namespaced keys (structural.dependency_cycle, dependency.feasibility, etc.)
 * are deprecated. New evaluations use CANONICAL_CHECK_KEYS.*
 */

// ── Governance layer check keys (Doc 102 §ConstraintCheckResult) ──────────────

/** Capability buckets and capacity windows exist for the plan version. */
export const CHECK_BUCKETS_MATERIALIZED = 'BucketsMaterialized' as const;

/** Demand does not exceed supply within the planning time window. */
export const CHECK_CAPACITY_FEASIBLE = 'CapacityFeasible' as const;

/** No unresolved circular or blocking dependencies. */
export const CHECK_DEPENDENCIES_SATISFIED = 'DependenciesSatisfied' as const;

/** No invalidated assumptions on the plan version. */
export const CHECK_ASSUMPTIONS_VALID = 'AssumptionsValid' as const;

/** No dropped initiatives remain in scope. */
export const CHECK_INITIATIVES_NOT_DROPPED = 'InitiativesNotDropped' as const;

/**
 * The plan version contains something to plan.
 *
 * Every other check is a statement about the version's contents, so an empty
 * version passes all of them vacuously — "No initiatives; eligibility N/A",
 * "No work units" — and a version holding nothing was the one thing that could
 * sail through a clean evaluation. This check is the floor: a version with no
 * initiatives and no work units fails outright.
 */
export const CHECK_VERSION_NOT_EMPTY = 'VersionNotEmpty' as const;

// ── Engine module check keys (extended set — Doc 108 §4.3) ───────────────────
// Used for Engine Module constraint evaluations and the Plan Health Summary widget.

/** Total allocated hours exceed available capacity for a resource type. */
export const CHECK_CAPACITY_OVERRUN = 'CapacityOverrun' as const;

/** Dependency graph contains a cycle. */
export const CHECK_DEPENDENCY_CYCLE = 'DependencyCycle' as const;

/** Required cross-functional team is not confirmed. */
export const CHECK_PARTICIPATION_GAP = 'ParticipationGap' as const;

/** An active assumption has been invalidated without a tradeoff. */
export const CHECK_ASSUMPTION_INVALIDATED = 'AssumptionInvalidated' as const;

/** Higher-priority items are scheduled after lower-priority items without accepted divergence. */
export const CHECK_PRIORITY_RANK_VIOLATED = 'PriorityRankViolated' as const;

/** Work units reference resource types with no capacity row defined. */
export const CHECK_NO_CAPACITY_MODEL = 'NoCapacityModel' as const;

/** Feasible end dates push past a milestone date. */
export const CHECK_MILESTONE_AT_RISK = 'MilestoneAtRisk' as const;

// ── Deprecated legacy keys (mapping table for backward compatibility) ─────────

export const LEGACY_KEY_MAP: Record<string, string> = {
  'structural.dependency_cycle': CHECK_DEPENDENCY_CYCLE,
  'dependency.feasibility': CHECK_DEPENDENCIES_SATISFIED,
  'capability.fit': CHECK_BUCKETS_MATERIALIZED,
  'capacity.feasibility': CHECK_CAPACITY_FEASIBLE,
  'recipe.pending': 'RecipePending',
  'policy.constraints': CHECK_ASSUMPTIONS_VALID,
};

/** Normalize a raw check_key (legacy or canonical) to the canonical form. */
export function normalizeCheckKey(key: string): string {
  return LEGACY_KEY_MAP[key] ?? key;
}

// ── Severity classification ───────────────────────────────────────────────────

/** Checks that block POR authorization when they fail. */
export const BLOCKING_CHECK_KEYS = new Set([
  CHECK_BUCKETS_MATERIALIZED,
  CHECK_CAPACITY_FEASIBLE,
  CHECK_DEPENDENCIES_SATISFIED,
  CHECK_DEPENDENCY_CYCLE,
  CHECK_CAPACITY_OVERRUN,
]);

/** Checks that are advisory warnings (do not block POR). */
export const ADVISORY_CHECK_KEYS = new Set([
  CHECK_ASSUMPTIONS_VALID,
  CHECK_INITIATIVES_NOT_DROPPED,
  CHECK_PARTICIPATION_GAP,
  CHECK_ASSUMPTION_INVALIDATED,
  CHECK_PRIORITY_RANK_VIOLATED,
  CHECK_NO_CAPACITY_MODEL,
  CHECK_MILESTONE_AT_RISK,
]);

export function getCheckSeverity(key: string): 'blocking' | 'advisory' {
  const normalized = normalizeCheckKey(key);
  return BLOCKING_CHECK_KEYS.has(normalized) ? 'blocking' : 'advisory';
}

/** Human-readable labels for display in Plan Health Summary. */
export const CHECK_KEY_LABELS: Record<string, string> = {
  [CHECK_BUCKETS_MATERIALIZED]: 'Capacity buckets materialized',
  [CHECK_CAPACITY_FEASIBLE]: 'Capacity feasibility',
  [CHECK_DEPENDENCIES_SATISFIED]: 'Dependencies satisfied',
  [CHECK_ASSUMPTIONS_VALID]: 'Assumptions valid',
  [CHECK_INITIATIVES_NOT_DROPPED]: 'Initiatives in scope',
  [CHECK_CAPACITY_OVERRUN]: 'Capacity overrun',
  [CHECK_DEPENDENCY_CYCLE]: 'Dependency cycle',
  [CHECK_PARTICIPATION_GAP]: 'Participation gap',
  [CHECK_ASSUMPTION_INVALIDATED]: 'Invalidated assumption',
  [CHECK_PRIORITY_RANK_VIOLATED]: 'Priority rank violated',
  [CHECK_NO_CAPACITY_MODEL]: 'Missing capacity model',
  [CHECK_MILESTONE_AT_RISK]: 'Milestone at risk',
  [CHECK_VERSION_NOT_EMPTY]: 'This version has nothing in it',
  // Legacy
  'RecipePending': 'Execution recipe pending',
};

/**
 * The same checks, named by what is WRONG when they fail.
 *
 * `CHECK_KEY_LABELS` names the check ("Dependencies satisfied"), which reads as
 * a pass when it is shown next to a failure. Anything reporting a violation —
 * the plan health list, an authorization refusal — wants this map instead.
 */
export const CHECK_FAILURE_LABELS: Record<string, string> = {
  DependenciesSatisfied: 'Blocking dependency unresolved',
  DependencyCycle: 'Dependency cycle in the work graph',
  RecipePending: 'Work unit has no work type',
  BucketsMaterialized: 'Capacity buckets not materialized',
  CapacityFeasible: 'Demand exceeds capacity',
  CapacityOverrun: 'Resource type over-allocated',
  NoCapacityModel: 'No capacity defined for a resource type',
  AssumptionsValid: 'Assumption invalidated',
  AssumptionInvalidated: 'Assumption invalidated without a tradeoff',
  InitiativesNotDropped: 'Dropped initiative still in scope',
  VersionNotEmpty: 'This version has nothing in it',
  PriorityRankViolated: 'Schedule violates the priority order',
  MilestoneAtRisk: 'Milestone at risk',
  ParticipationGap: 'Required team unconfirmed',
};
